/**
 * Parses server 402 gate responses from the instructor entitlement engine.
 */

import { INVITE_TEAM_MIN_TIER } from './instructorMeterLimits';
import { FEATURE_MIN_TIER, tierRank } from './instructorPlanTier';

function formatMinTierLabel(tierSlug) {
  if (!tierSlug) {
    return 'Organization';
  }
  return tierSlug.charAt(0).toUpperCase() + tierSlug.slice(1);
}

/** Per-feature requirement copy, so the dialog subtitle never repeats the title. */
const FEATURE_REQUIREMENT_COPY = {
  invite_team_members: (tier) => `Team invites require the ${tier} plan or higher.`,
  ai_course_builder: (tier) => `AI course builder requires the ${tier} plan or higher.`,
  scorm_training: (tier) => `SCORM and video training require the ${tier} plan or higher.`,
  api_access: (tier) => `Programmatic API access requires the ${tier} plan.`,
  custom_ai_tutor_persona: (tier) => `Custom AI tutor persona requires the ${tier} plan.`,
};

/**
 * True when the viewer's plan already meets the gate's required tier. The block is then a role
 * restriction, not a billing problem — sending these users to checkout would sell them a plan
 * they already have.
 */
export function isRoleRestrictedGate(body) {
  const gate = parseGateBody(body);
  if (gate.error !== 'feature_locked') {
    return false;
  }
  if (gate.feature === 'billing_manage') {
    return true;
  }
  const requiredRank = tierRank(gate.upgrade_to || FEATURE_MIN_TIER[gate.feature]);
  const currentRank = tierRank(gate.current_tier);
  return currentRank >= 0 && requiredRank >= 0 && currentRank >= requiredRank;
}

export function isGateStatus(status) {
  return status === 402;
}

export function parseGateBody(body) {
  if (!body || typeof body !== 'object') {
    return {
      error: 'limit_reached',
      message: 'Upgrade required',
    };
  }
  return body;
}

/** Resolves the upgrade dialog title from a gate payload. */
export function gateTitle(body) {
  const gate = parseGateBody(body);
  if (gate.error === 'feature_locked') {
    return isRoleRestrictedGate(gate) ? 'Access restricted' : 'Upgrade required';
  }
  if (gate.error === 'limit_reached') {
    return 'Plan limit reached';
  }
  return gate.message === 'Upgrade required' ? 'Upgrade required' : 'Plan limit reached';
}

export function gateMessage(body) {
  const gate = parseGateBody(body);
  if (gate.error === 'feature_locked' && gate.feature === 'billing_manage') {
    return 'Billing changes are managed by your organization admin.';
  }
  if (gate.error === 'feature_locked' && isRoleRestrictedGate(gate)) {
    // The plan already clears the requirement, so the block is a role or per-plan override —
    // stay neutral about which rather than blaming a plan the viewer already pays for.
    return 'This feature is not enabled for your account. '
      + 'Your organization owner or StudYaK support can turn it on.';
  }
  if (gate.error === 'feature_locked' && FEATURE_REQUIREMENT_COPY[gate.feature]) {
    const fallbackTier = gate.feature === 'invite_team_members'
      ? INVITE_TEAM_MIN_TIER
      : FEATURE_MIN_TIER[gate.feature];
    const minTier = formatMinTierLabel(gate.upgrade_to || fallbackTier);
    return FEATURE_REQUIREMENT_COPY[gate.feature](minTier);
  }
  if (gate.meter === 'inst.courses') {
    return 'You have reached the course limit for your plan.';
  }
  if (gate.meter === 'inst.students') {
    return 'You have reached the student enrollment limit for your plan.';
  }
  if (gate.meter === 'inst.assessments') {
    return 'You have reached the assessment limit for your plan.';
  }
  return gate.message || 'Upgrade required to continue.';
}

/**
 * Reads a fetch Response; returns { gate: true, body } when status is 402.
 */
export async function readResponseOrGate(response) {
  if (isGateStatus(response.status)) {
    let body = {};
    try {
      body = await response.json();
    } catch (_) {
      body = { message: 'Upgrade required' };
    }
    return { gate: true, body: parseGateBody(body) };
  }
  return { gate: false, response };
}
